import { call, put } from 'redux-saga/effects';
import {
  fetchAllExcelTransactionsFailure,
  fetchAllExcelTransactionsSuccess,
} from '../actions';
import { getAllExcelTransactions } from '../api';
import { FetchAllExcelTransactionsRequestAction } from '../types';
import { MessageData } from 'store/app/types';
import { turnMessageOn } from 'store/app/actions';
import { logoutRequest } from 'store/auth/actions';
import { renderErrorMessage } from 'utils/render-error-message';
import type { ValidatedExcelTransaction } from '../models';
import type { ExcelTransaction } from '../models';
import { normalizeExcelTransactionFromApi } from '../reducer/excelHandlers';

function toValidatedTransaction(
  row: ExcelTransaction
): ValidatedExcelTransaction {
  return { ...normalizeExcelTransactionFromApi(row), errorReason: '' };
}

export function* fetchAllExcelTransactionsSaga(
  action: FetchAllExcelTransactionsRequestAction
) {
  // Destructure outside try/catch so these vars are always in scope,
  // even when an error is thrown early (e.g. in getAllExcelTransactions).
  const { organisationId, page, pageSize, filters } = action.payload;
  if (!organisationId) {
    // Guard against transient empty organisation IDs during page refresh/navigation.
    return;
  }

  try {
    // Convert filters from ScopeTable's internal shape into a plain map,
    // consistent with how VAT report transactions are sent.
    const convertFiltersToMap = (fs?: Record<string, any>) => {
      if (!fs) return undefined;
      const result: Record<string, any> = {};
      Object.keys(fs).forEach((key) => {
        const filterObject = fs[key];
        if (filterObject && Array.isArray(filterObject.value)) {
          result[key] = filterObject.value;
        }
      });
      return Object.keys(result).length ? result : undefined;
    };
    const convertedFilters = convertFiltersToMap(filters as any);
    const response = yield call(
      getAllExcelTransactions,
      organisationId,
      page,
      pageSize,
      convertedFilters
    );
    const transactionsData = response?.data?.transactions;
    const content: ExcelTransaction[] = transactionsData?.content ?? [];
    const total = transactionsData?.totalElements ?? 0;
    const transactions: ValidatedExcelTransaction[] = content.map(
      toValidatedTransaction
    );
    yield put(
      fetchAllExcelTransactionsSuccess(
        organisationId,
        transactions,
        total,
        page,
        transactionsData?.pageSize ?? pageSize
      )
    );
  } catch (error: any) {
    const message: MessageData | null = renderErrorMessage(error);
    if (message) {
      yield put(turnMessageOn(message));
    } else {
      yield put(logoutRequest());
    }
    // Clear any previously loaded allExcelTransactionsPage so the table doesn't
    // keep showing stale data when the backend request fails.
    yield put(
      fetchAllExcelTransactionsSuccess(organisationId, [], 0, page, pageSize)
    );
    yield put(fetchAllExcelTransactionsFailure());
  }
}
